import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import { ClickAwayListener, useThemeProps } from "@mui/material";
import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useSlotProps } from "../../base/index.js";
import { timePickerName } from "./classes.js";
import { useOptions } from "./hooks/useOptions.js";
import { useScrollToSelectedItem } from "./hooks/useScrollToSelectedItem.js";
import { useTimePicker } from "./hooks/useTimePicker.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
import { MenuItem } from "./components/MenuItem.js";
import { MenuList } from "./components/MenuList.js";
import { MenuPaper } from "./components/MenuPaper.js";
import { Popper } from "./components/Popper.js";
import { Root } from "./components/Root.js";
const defaultPopperModifiers = [
  {
    name: "flip",
    enabled: true,
    options: {
      altBoundary: true,
      rootBoundary: "document"
    }
  }
];
function createTimePickerOptions(interval = 15) {
  const lastValidTimeOption = 1440;
  const nextValues = [0];
  while (nextValues[nextValues.length - 1] + interval < lastValidTimeOption) {
    nextValues.push(nextValues[nextValues.length - 1] + interval);
  }
  return nextValues;
}
function useCreateOptions(optionValues) {
  return useMemo(() => {
    if (typeof optionValues === "function") return optionValues();
    if (Array.isArray(optionValues)) return optionValues;
    return createTimePickerOptions(optionValues);
  }, [optionValues]);
}
const TimePicker = forwardRef(function TimePicker2(inProps, ref) {
  const props = useThemeProps({
    name: timePickerName,
    props: inProps
  });
  const {
    sx,
    name,
    value = null,
    label,
    error: externalError,
    format: timeFormat,
    testId = `${timePickerName}-root`,
    onChange,
    required,
    disabled = false,
    className = "",
    allowEmpty = false,
    components,
    helperText,
    errorLabels,
    optionValues: optionValuesProp = 15,
    errorVisibility = "all",
    componentsProps,
    allowUnlistedValues = false
  } = props;
  const format = timeFormat === "12" ? "hh:mm aa" : "HH:mm";
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const optionValues = useCreateOptions(optionValuesProp);
  const optionsRef = useRef(optionValues);
  useEffect(() => void (optionsRef.current = optionValues), [optionValues]);
  const {
    actions,
    componentState: { open, inputValue, invalidInput, selectValue, error: componentError }
  } = useTimePicker(format, optionValues, value, onChange, allowUnlistedValues, allowEmpty, disabled, inputRef);
  const dropdownOptions = useOptions(optionValues, format);
  useScrollToSelectedItem(open, format, inputValue, selectValue, menuRef, optionsRef, allowEmpty);
  const { mouseActions, focusActions, keyboardActions, changeActions } = actions;
  const showErrorText = errorVisibility === "all" || errorVisibility === "text-only";
  const showErrorAccent = errorVisibility === "all" || errorVisibility === "accent-only";
  const fieldHasError = !!componentError || !!externalError;
  const errorText = componentError ? errorLabels[componentError] : externalError;
  const gotErrors = invalidInput || fieldHasError;
  const errorOrHelperText = showErrorText && fieldHasError ? errorText : helperText;
  const showHelperText = !!errorOrHelperText;
  const helperTextProp = showHelperText ? errorOrHelperText : void 0;
  const ownerState = props;
  const utilityClasses = useUtilityClasses(ownerState);
  const rootSlotProps = useSlotProps({
    className: `${className} ${utilityClasses.root}`,
    ownerState,
    elementType: Root,
    additionalProps: {
      sx,
      as: components?.Root,
      ref,
      "data-tidal": timePickerName,
      "data-testid": testId,
      label,
      name,
      inputRef,
      value: inputValue,
      error: showErrorAccent && gotErrors,
      onBlur: focusActions.onBlur,
      onFocus: focusActions.onFocus,
      onClick: mouseActions.onClick,
      onChange: changeActions.onInputChange,
      required,
      disabled,
      onKeyDown: keyboardActions.onKeyDown,
      helperText: helperTextProp
    },
    externalSlotProps: componentsProps?.root
  });
  const popperSlotProps = useSlotProps({
    className: utilityClasses.popper,
    ownerState,
    elementType: Popper,
    additionalProps: {
      as: components?.Popper,
      ref: menuRef,
      open,
      anchorEl: inputRef.current,
      modifiers: defaultPopperModifiers,
      placement: "bottom-start"
    },
    externalSlotProps: componentsProps?.popper
  });
  const menuPaperSlotProps = useSlotProps({
    className: utilityClasses.menuPaper,
    ownerState,
    elementType: MenuPaper,
    additionalProps: {
      as: components?.MenuPaper,
      elevation: 4,
      inputWidth: inputRef.current?.offsetWidth
    },
    externalSlotProps: componentsProps?.menuPaper
  });
  const menuListSlotProps = useSlotProps({
    className: utilityClasses.menuList,
    ownerState,
    elementType: MenuList,
    additionalProps: {
      as: components?.MenuList,
      disableListWrap: true
    },
    externalSlotProps: componentsProps?.menuList
  });
  const menuItemSlotProps = useSlotProps({
    className: utilityClasses.menuItem,
    ownerState,
    elementType: MenuItem,
    additionalProps: { as: components?.MenuItem },
    externalSlotProps: componentsProps?.menuItem
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Root, { ...rootSlotProps }),
    /* @__PURE__ */ jsx(Popper, { ...popperSlotProps, children: /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: mouseActions.onClickAway, children: /* @__PURE__ */ jsx(MenuPaper, { ...menuPaperSlotProps, children: /* @__PURE__ */ jsx(MenuList, { ...menuListSlotProps, children: dropdownOptions.map((option) => /* @__PURE__ */ createElement(
      MenuItem,
      {
        ...menuItemSlotProps,
        key: option.value,
        value: option.value,
        onClick: changeActions.onSelectChange,
        selected: selectValue === option.value,
        "data-selected": selectValue === option.value
      },
      option.label
    )) }) }) }) })
  ] });
});
export {
  TimePicker
};
//# sourceMappingURL=TimePicker.js.map
